import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import ChevronDown from 'assets/svg/ChevronDown';
import type { CalendarEvent, CalendarRow, ChangeType, Frequency, Period } from '../../../../types/calendar';
import { ACTION_COLUMN_WIDTH, COLUMN_WIDTH, PERIOD_CELL_STYLE } from '../../../../config/calendarConstants';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { CHANGE_TYPE_CONFIG } from '../../../../config/changeTypeConfig';
import { CALENDAR_CELL_RENDERERS, renderPromoName } from '../../cellRenderers';
import { calculatePeriodColumnWidth, formatPeriodLabel } from '../../utils/periodLayout';
import PeriodCell from '../../components/PeriodCell';

const CHANGE_INDICATOR_WIDTH = 48;

export interface UseCalendarColumnsParams {
    periods: Period[];
    frequency: Frequency;
    containerWidth: number;
    expandedRowKeys: string[];
    setExpandedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
    onClickInterval: () => void;
    selectedColumnKeys: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    getColumnSortOrder: (dataIndex: string) => 'ascend' | 'descend' | null;
    getRowActionMenuItems: (record: CalendarRow) => MenuProps['items'];
    showChanges: boolean;
    onEditEvent: (record: CalendarRow) => void;
    onEditPromo: (record: CalendarRow) => void;
}

const isEditableRow = (record: CalendarRow): boolean => !record.children;

export function useCalendarColumns({
    periods,
    frequency,
    containerWidth,
    expandedRowKeys,
    setExpandedRowKeys,
    onClickInterval,
    selectedColumnKeys,
    getColumnSortOrder,
    getRowActionMenuItems,
    showChanges,
    onEditEvent,
    onEditPromo,
}: UseCalendarColumnsParams) {
    const { allDefaultColumns } = useTpoGenConfig();
    const visibleDefaultColumns = useMemo(
        () =>
            allDefaultColumns.filter((col) => selectedColumnKeys.includes(col.key)).map((col) => {
                const withSort = col.sorter ? { sortOrder: getColumnSortOrder(col.dataIndex) } : {};
                const baseRender = CALENDAR_CELL_RENDERERS[col.key];

                if (col.key === 'event_name') {
                    return {
                        ...col,
                        ...withSort,
                        title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                        render: (text: string, record: CalendarRow) => {
                            const isEditable = isEditableRow(record) && !!record.event_id;
                            const nameNode = (
                                <span
                                    className={`truncate text-sm ${
                                        record.children ? 'font-semibold' : 'font-normal'
                                    } ${isEditable ? 'text-purpleFont cursor-pointer underline' : ''}`}
                                    onClick={(e) => {
                                        if (!isEditable) return;
                                        e.stopPropagation();
                                        onEditEvent(record);
                                    }}
                                >
                                    {text}
                                </span>
                            );

                            return (
                                <div className="flex items-center">
                                    {record.children ? (
                                        <span
                                            className="cursor-pointer mr-2 text-xs flex-shrink-0"
                                            onClick={() =>
                                                setExpandedRowKeys((prev) =>
                                                    prev.includes(record.key)
                                                        ? prev.filter((k: string) => k !== record.key)
                                                        : [...prev, record.key],
                                                )
                                            }
                                        >
                                            <ChevronDown
                                                className={
                                                    expandedRowKeys.includes(record.key) ? 'rotate-180' : 'rotate-0'
                                                }
                                                size={12}
                                            />
                                        </span>
                                    ) : null}
                                    {isEditable ? (
                                        <Tooltip title="Click to edit" showArrow={false}>
                                            {nameNode}
                                        </Tooltip>
                                    ) : (
                                        nameNode
                                    )}
                                </div>
                            );
                        },
                    };
                }

                if (col.key === 'promo_name') {
                    return {
                        ...col,
                        ...withSort,
                        title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                        render: (text: string, record: CalendarRow) => {
                            const isEditable = isEditableRow(record) && !!record.promo_id;
                            return (
                                <div
                                    className={isEditable ? 'cursor-pointer' : ''}
                                    onClick={(e) => {
                                        if (!isEditable) return;
                                        e.stopPropagation();
                                        onEditPromo(record);
                                    }}
                                >
                                    {renderPromoName(text, record, isEditable)}
                                </div>
                            );
                        },
                    };
                }

                return {
                    ...col,
                    ...withSort,
                    title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                    render: baseRender,
                };
            }),
        [
            allDefaultColumns,
            selectedColumnKeys,
            expandedRowKeys,
            getColumnSortOrder,
            setExpandedRowKeys,
            onEditEvent,
            onEditPromo,
        ],
    );

    const changeIndicatorColumn = useMemo(
        () =>
            showChanges
                ? {
                      key: 'change_indicator',
                      title: '',
                      dataIndex: 'promo_lifecycle_status',
                      fixed: 'left' as const,
                      width: CHANGE_INDICATOR_WIDTH,
                      render: (changeType: ChangeType | null | undefined, record: CalendarRow) => {
                          if (record.children || !changeType) return null;
                          const config = CHANGE_TYPE_CONFIG[changeType];
                          if (!config) return null;
                          const Icon = config.icon;
                          return (
                              <div className="flex items-center justify-center">
                                  <Icon size={16} />
                              </div>
                          );
                      },
                  }
                : null,
        [showChanges],
    );

    const fixedColumnsWidth =
        visibleDefaultColumns.length * COLUMN_WIDTH + (changeIndicatorColumn ? CHANGE_INDICATOR_WIDTH : 0);

    const periodColumns = useMemo(() => {
        if (!periods.length) return [];

        const cellWidth = calculatePeriodColumnWidth(periods.length, frequency, containerWidth, fixedColumnsWidth);
        const firstPeriod = periods[0];
        const daysPerCell = dayjs(firstPeriod.period_end_date).diff(dayjs(firstPeriod.period_start_date), 'day') || 7;
        const pxPerDay = cellWidth / daysPerCell;

        return periods.map((period) => ({
            key: period.period_start_date,
            title: (
                <p className="text-sm text-[#18181B] truncate font-semibold">
                    {formatPeriodLabel(period.period_start_date, frequency)}
                </p>
            ),
            dataIndex: period.period_start_date,
            width: cellWidth,
            onCell: () => ({
                style: PERIOD_CELL_STYLE,
            }),
            ellipsis: true,
            render: (events: CalendarEvent[], record: CalendarRow) =>
                record.children ? null : (
                    <PeriodCell
                        events={events ?? []}
                        frequency={frequency}
                        pxPerDay={pxPerDay}
                        periodStartDate={period.period_start_date}
                        cellWidth={cellWidth}
                    />
                ),
        }));
    }, [periods, frequency, containerWidth, fixedColumnsWidth]);

    const actionColumn = useMemo(
        () => ({
            key: 'actions',
            title: <MoreOutlined style={{ fontSize: 16, cursor: 'pointer' }} onClick={onClickInterval} />,
            fixed: 'right' as const,
            width: ACTION_COLUMN_WIDTH,
            render: (_: unknown, record: CalendarRow) => {
                if (record.children) return null;
                return (
                    <div className="flex items-center justify-center">
                        <Dropdown
                            menu={{ items: getRowActionMenuItems(record) }}
                            trigger={['click']}
                            overlayStyle={{ width: '10.9375rem', borderRadius: '0.5rem' }}
                        >
                            <MoreOutlined style={{ fontSize: 16, cursor: 'pointer' }} />
                        </Dropdown>
                    </div>
                );
            },
        }),
        [onClickInterval, getRowActionMenuItems],
    );

    return useMemo(
        () => [
            ...(changeIndicatorColumn ? [changeIndicatorColumn] : []),
            ...visibleDefaultColumns,
            ...periodColumns,
            actionColumn,
        ],
        [changeIndicatorColumn, visibleDefaultColumns, periodColumns, actionColumn],
    );
}
